'use client'

import React, { useMemo, useState } from 'react'
import { ColDef, FilterModel, GetRowIdFunc, GetRowIdParams } from '@ag-grid-community/core'
import { AGGridWrapper, useAGGWrapperContext } from '@fsg/gui-bits'
import { useAppContextSelector } from '@app/components/AppContext'
import { GridHeader } from '@app/components/GridHeader'
import {
  LeadStatus,
  MarketingSource,
  MarketingSummaryResponse,
  OpportunitiesEnums,
  OpportunityResponse,
  QualityRating,
  ENTITY,
} from '@app/types'
import { convertFilterModelToApiQuery, GridColDef } from '@app/utils'
import { getShortDate } from '@app/utils/dates'

import { MarketingSummaryKPIs } from './MarketingSummaryKPIs'
import { useMarketingSummaryService } from '../../../lib/hooks'
import { useGridManager } from '@app/hooks/grid/useGridManager'
import {useRequestCallback} from '@fsg/next-auth'

type Props = {
  enums: OpportunitiesEnums
  count: number
  qualityRatings: QualityRating[]
  sources: MarketingSource[]
  leadStatus: LeadStatus[]
}

const MARKETING_SUMMARY_TABLE_ID = 'MARKETING_SUMMARY_TABLE'

export function MarketingSummaryDashboard({ enums: ENUMS, count: initialCount, qualityRatings, sources, leadStatus }: Props) {
  const service = useMarketingSummaryService()
  const divisions: any = useAppContextSelector((state) => state.divisions)
  const [kpiQuery, setKPIquery] = useState('')
const {request} = useRequestCallback()
  const statusFilterValues = useMemo(() => {
    return leadStatus.map(({ status }) => status).concat(Object.keys(ENUMS.opportunityStages))
  }, [])

  const statusFilterModel = useMemo<FilterModel>(() => {
    return {
      status: {
        filterType: 'set',
        values: statusFilterValues.filter((status) => status !== 'Qualified Lead'),
      },
    }
  }, [])

  const gridManager = useGridManager<MarketingSummaryResponse>(MARKETING_SUMMARY_TABLE_ID, service, {
    initialValues: { view: 'all', count: initialCount, filterModel: statusFilterModel },
  })

  const columnDefs = React.useMemo<ColDef[]>(() => {
    return [
      GridColDef.entityId(),
      GridColDef.MarketingSummary.linkToEntity(),
      GridColDef.MarketingSummary.type(), // fake field
      GridColDef.MarketingSummary.entityName(), // opp.name || lead.fullName || lead.company
      GridColDef.MarketingSummary.customer(), //opp.account.name || lead.company
      GridColDef.MarketingSummary.fsgOwner(), // opp.
      GridColDef.MarketingSummary.division(divisions.data),
      GridColDef.MarketingSummary.status(statusFilterValues), // lead.status || opp.stage
      GridColDef.MarketingSummary.source(sources), // source
      GridColDef.MarketingSummary.campaign(), // opp.campaign
      GridColDef.MarketingSummary.estimatedValue(), // opp.estimatedRevenue || lead.estimatedRevenue
      GridColDef.MarketingSummary.probableValue(),
      //GridColDef.MarketingSummary.quoted(),
      GridColDef.salesType(ENTITY.OPPORTUNITIES, request, ENUMS), // opp.salesType
      GridColDef.createdAt(),
    ]
  }, [ENUMS, divisions.data, qualityRatings, sources])

  const getRowId = React.useMemo<GetRowIdFunc>(() => {
    return (params: GetRowIdParams<OpportunityResponse>) => params.data.id
  }, [])

  const header = (change: boolean) => <Header filterModelChanged={change} count={gridManager.count} defaultFilterModel={statusFilterModel} />

  const context = {
    allGridManager: gridManager,
  }

  return (
    <>
      <MarketingSummaryKPIs query={kpiQuery} count={gridManager.count} />
      <AGGridWrapper
        context={context}
        gridRef={gridManager.gridRef}
        getRowId={getRowId}
        rowModelType="serverSide"
        header={header(gridManager.filterModelChanged)}
        columnDefs={columnDefs}
        {...gridManager.gridOptions}
        onFilterChanged={(params) => {
          const filterModel = params.api.getFilterModel()

          const query = convertFilterModelToApiQuery(filterModel)
          setKPIquery(query)
        }}
      />
    </>
  )
}

type HeaderProps = {
  count: number
  filterModelChanged: boolean
  defaultFilterModel: FilterModel
}

function Header(props: HeaderProps) {
  const { gridRef } = useAGGWrapperContext()
  const gridApi = gridRef.current?.api

  const onClickReset = () => gridApi.setFilterModel(props.defaultFilterModel)

  const exportCsvTitle = React.useMemo(() => {
    return `MarketingSummary - ${getShortDate()}`
  }, [])

  return (
    <GridHeader>
      <GridHeader.FilterButton label="Leads and Opportunities" active count={props.count} />
      <GridHeader.Spacer />
      {props.filterModelChanged && <GridHeader.ResetFilters onClick={onClickReset} />}
      <GridHeader.ExportCSV fileName={exportCsvTitle} />
    </GridHeader>
  )
}
